// Lib
import React from 'react';
import PropTypes from 'prop-types';
import shallowEqual from 'shallowequal';

// Utils
import { roundToPixel } from '../../utils/maths/clientPoint';
import * as pointLib from '../../../common/maths/geometry/point';
import * as matrixLib from '../../../common/maths/matrix/matrixUtils';
import { asObject } from '../../../common/utils/immutableHelper';

// Hooks
import usePrevious from '../../utils/react/usePrevious';

// Constants
import { ZOOM_MODES } from './zoomConstants';

/**
 * This handles the logic to update the scroll position of the canvas when the top left position
 * of the canvas origin changes, so that the elements on the canvas don't visually change their position.
 */
const CanvasScrollPositionHandler = (props) => {
    const {
        gridSize,
        zoomMode,
        zoomScale,
        canvasOrigin,
        canvasViewportRef,
        currentBoardId,
        getZoomCanvasTransformationMatrix,
        setCanvasDocumentTranslation,
    } = props;

    const prevCurrentBoardId = usePrevious(currentBoardId);
    const prevCanvasOrigin = usePrevious(canvasOrigin);

    /**
     * When the canvas origin changes, we want to offset the change using scroll to
     * ensure that the canvas doesn't appear to jump.
     *
     * If we didn't do this, elements on the canvas would appear to get pushed down to the
     * bottom right, because the top left position of the canvas is being extended.
     */
    React.useLayoutEffect(() => {
        if (props.isShowingTemplate) return;

        // On board change, ignore changes in the canvas origin
        if (currentBoardId !== prevCurrentBoardId) return;

        // Only when the canvas origin changes
        if (shallowEqual(asObject(canvasOrigin), asObject(prevCanvasOrigin))) {
            return;
        }

        const transformationMatrix = getZoomCanvasTransformationMatrix();

        const originDiff = pointLib.difference(prevCanvasOrigin, canvasOrigin);
        const originDiffPx = pointLib.scale(gridSize, originDiff);
        const originDiffScaledPx = roundToPixel(pointLib.scale(zoomScale, originDiffPx));

        const currentScrollLeft = canvasViewportRef.current.scrollLeft;
        const currentScrollTop = canvasViewportRef.current.scrollTop;

        // Not sure why, but during some moves the scroll can't immediately be updated.
        // The scroll area seems to be delayed by a frame, so use a requestAnimationFrame to
        // ensure that it's updated correctly.
        // This appears to work for both creates and moves, and useLayoutEffect vs useEffect
        // doesn't make any difference
        requestAnimationFrame(() => {
            const scrollLeftDiff = currentScrollLeft - originDiffScaledPx.x;
            const scrollTopDiff = currentScrollTop - originDiffScaledPx.y;

            const nextScrollLeft = Math.max(scrollLeftDiff, 0);
            const nextScrollTop = Math.max(scrollTopDiff, 0);

            canvasViewportRef.current.scrollLeft = nextScrollLeft;
            canvasViewportRef.current.scrollTop = nextScrollTop;

            const translationDiff = {
                x: Math.max(-scrollLeftDiff, 0),
                y: Math.max(-scrollTopDiff, 0),
            };

            // If there's no difference in translation, we don't need to update the transformation matrix
            if (!translationDiff.x && !translationDiff.y) return;

            const currentScale = matrixLib.getScale(transformationMatrix);
            const currentTranslation = matrixLib.getTranslation(transformationMatrix);

            const ignoreTranslationChanges =
                currentScale >= 1 &&
                currentTranslation.x === 0 &&
                currentTranslation.y === 0 &&
                zoomMode !== ZOOM_MODES.ZOOM_TO_FIT;

            // If we're not zoomed out, then don't translate the document
            if (ignoreTranslationChanges) return;

            const nextTranslation = pointLib.translate(currentTranslation, translationDiff);

            setCanvasDocumentTranslation(nextTranslation);
        });
    }, [canvasOrigin, prevCanvasOrigin, currentBoardId, prevCurrentBoardId, zoomMode]);

    return null;
};

CanvasScrollPositionHandler.propTypes = {
    currentBoardId: PropTypes.string,
    gridSize: PropTypes.number,
    zoomMode: PropTypes.string,
    zoomScale: PropTypes.number,
    canvasOrigin: PropTypes.object,
    isShowingTemplate: PropTypes.bool,

    canvasViewportRef: PropTypes.object,

    getZoomCanvasTransformationMatrix: PropTypes.func,
    setCanvasDocumentTranslation: PropTypes.func,
};

export default CanvasScrollPositionHandler;
